// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import { isHexTriplet, type HexTriplet } from '@evolvemep/api-ts';
import { ColorPicker, Box, Popover, ColorSwatch, InputLabel, ActionIcon } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import barClasses from 'components/Mantine/ColorPickers/color-picker.module.css';

type Props = {
  type?: 'bar' | 'dot';
  label?: string;
  value: HexTriplet;
  onChange: (value: HexTriplet) => void;
  transparent?: boolean;
  disabled?: boolean;
};

export const WrappedColorPicker = ({
  type = 'dot',
  label = 'Color',
  value,
  onChange,
  transparent,
  disabled,
}: Props) => {
  const [opened, setOpened] = useState(false);
  const [internalColor, setInternalColor] = useUncontrolled<string>({ defaultValue: value });

  const displayColor = `${value}${transparent ? '88' : ''}`;

  return (
    <Popover onChange={setOpened} opened={opened}>
      <AgGridStyleTooltip label={`Change ${label}`} withArrow hidden={opened}>
        <Popover.Target>
          {type === 'bar' ? (
            <Box w="0.5em" style={{ overflow: 'visible' }}>
              <ColorSwatch
                className={`${barClasses.colorBox} ${opened ? barClasses.active : ''}`}
                color={displayColor}
                radius={0}
                size="0.5em"
                withShadow={false}
                onClick={() => (!disabled ? setOpened((o) => !o) : undefined)}
              />
            </Box>
          ) : (
            <ActionIcon variant="outline" color="gray.4" onClick={() => setOpened((o) => !o)} disabled={disabled}>
              <ColorSwatch size={10} color={displayColor} withShadow={false} />
            </ActionIcon>
          )}
        </Popover.Target>
      </AgGridStyleTooltip>
      <Popover.Dropdown>
        <ActionIcon onClick={() => setOpened(false)} style={{ position: 'absolute', right: 10, top: 10 }}>
          <EvolveIcon icon="Close" size="sm" color="dark" />
        </ActionIcon>
        <InputLabel mb={4}>{label}</InputLabel>
        <ColorPicker
          value={internalColor}
          onChange={setInternalColor}
          onChangeEnd={(newValue) => onChange(isHexTriplet(newValue) ? newValue : value)}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
